/**
 * In-app PDF viewer (react-pdf), aligned with medonc / pharmacy doc viewer modals.
 */

import { RotateCw } from "lucide-react";
import { useLayoutEffect, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

// Served from our own origin: the dashboard CSP allows `worker-src 'self'`
// only. `scripts/copy-pdf-worker.js` stages this file from the pdfjs-dist copy
// react-pdf depends on, so the worker version always matches the API version.
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

/** Props for {@link PdfRender}. */
export interface PdfRenderProps {
  file?: File | string;
  hideRotateButtons?: boolean;
  hideZoomButtons?: boolean;
  backgroundColor?: string;
  className?: string;
}

/**
 * Render a PDF with zoom and rotate controls inside the host panel.
 *
 * @param props - PDF source and toolbar options
 * @returns Scrollable multi-page PDF viewer
 */
export default function PdfRender({
  file,
  hideRotateButtons,
  hideZoomButtons,
  backgroundColor,
  className,
}: PdfRenderProps) {
  const [numPages, setNumPages] = useState<number>();
  const [scale, setScale] = useState(1.0);
  const [rotation, setRotation] = useState(0);
  const [pdfKey, setPdfKey] = useState(0);

  useLayoutEffect(() => {
    setPdfKey((prevKey) => prevKey + 1);
  }, [file]);

  /**
   * Store total pages after the document loads.
   *
   * @param payload - react-pdf load success payload
   */
  function onDocumentLoadSuccess({ numPages: pages }: { numPages: number }): void {
    setNumPages(pages);
  }

  const zoomIn = () => setScale((prev) => Math.min(prev + 0.2, 3));
  const zoomOut = () => setScale((prev) => Math.max(prev - 0.2, 0.5));
  const rotateClockwise = () => setRotation((prev) => (prev + 90) % 360);
  const rotateCounterClockwise = () =>
    setRotation((prev) => (prev - 90 + 360) % 360);

  return (
    <div
      className={`pdf-render__container flex h-full w-full flex-col overflow-hidden ${className ?? ""}`}
      style={backgroundColor ? { backgroundColor } : undefined}
    >
      <div className="pdf-render__actions relative shrink-0 px-2 pt-2">
        <div className="mb-2 flex items-center justify-between gap-2">
          {!hideZoomButtons && (
            <div className="flex h-full items-center gap-1.5 rounded-lg bg-white/90 px-3 py-1.5 shadow-lg backdrop-blur-sm">
              <button
                type="button"
                onClick={zoomOut}
                className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-200"
              >
                -
              </button>
              <span className="min-w-[50px] text-center text-xs font-medium text-gray-700">
                {Math.round(scale * 100)}%
              </span>
              <button
                type="button"
                onClick={zoomIn}
                className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-200"
              >
                +
              </button>
            </div>
          )}

          {!hideRotateButtons && (
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={rotateCounterClockwise}
                className="flex items-center justify-center rounded-full border border-primaryGray-14 bg-white p-2 text-sm font-medium transition-colors hover:bg-gray-200"
                aria-label="Rotate counter-clockwise"
              >
                <RotateCw className="h-3.5 w-3.5 -scale-x-100" />
              </button>
              <button
                type="button"
                onClick={rotateClockwise}
                className="flex items-center justify-center rounded-full border border-primaryGray-14 bg-white p-2 text-sm font-medium transition-colors hover:bg-gray-200"
                aria-label="Rotate clockwise"
              >
                <RotateCw className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="pdf-render__document-viewer min-h-0 flex-1 overflow-auto bg-primaryGray-16">
        <div className="w-full" key={pdfKey}>
          <Document
            key={pdfKey}
            file={file}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={(error) => {
              console.error("Error while loading PDF", error);
            }}
            className="my-react-pdf"
          >
            {Array.from(new Array(numPages), (_el, index) => (
              <Page
                key={`${index}-${pdfKey}`}
                pageNumber={index + 1}
                scale={scale}
                rotate={rotation}
                className="mb-4 border border-primaryGray-14 shadow-md"
              />
            ))}
          </Document>
        </div>
      </div>
    </div>
  );
}
